import type { ServicePageConfig } from "../../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../../package-pricing";
import { getCityMunicipalityList } from "../../../cities";

const goteborgKommunerText = getCityMunicipalityList("göteborg");

// ─── Flytthjälp Alingsås ───────────────────────────────────────────
export const flytthjalpAlingsasPage: ServicePageConfig = {
  slug: "flytthjalp-alingsas",
  serviceCategory: "flytt",
  city: "göteborg",

  meta: {
    title: "Flytthjälp Alingsås – Flytt med Skåpbil | Ungdomskraft",
    description:
      "Boka flytthjälp i Alingsås, Ingared, Sollebrunn och Västerbodarna. Skåpbil och bärare ingår, priset syns direkt och RUT-avdraget dras på fakturan.",
    h1: "Flytthjälp Alingsås",
  },

  hero: {
    heading: "Flytthjälp i Alingsås",
    intro:
      "Flyttar du till, från eller inom Alingsås? Vi kommer med skåpbil och bärare till trähusen i centrum, lägenheterna på Brogården, radhusen och villorna i stadsdelarna runt centrum, villaområdena i Ingared vid Sävelången eller gårdarna runt Sollebrunn och Gräfsnäs. Hela flytten i ett paket – priset syns innan du bokar och RUT-avdraget görs direkt på fakturan.",
    heroImage: "/hero-flytthjalp.png",
    heroImageAlt: "Ungdomskraft lastar möbler i skåpbil vid flytthjälp i Alingsås",
  },

  featureCards: {
    sinceText: "Flytthjälp sedan 2014",
    storyText:
      "Ungdomskraft har flyttat hushåll i Göteborgsregionen sedan 2014. Alingsås ligger i regionens nordöstra hörn, och vi kör E20 hit regelbundet – både med familjer som lämnar Göteborg och med alingsåsare som byter bostad inom kommunen.",
    bookYourText: "Boka din flytt",
    bookDescText:
      "Fyll i adresserna, välj vad som ska flyttas och en dag som passar. Körsträckan räknas in direkt, så priset du ser är priset du betalar.",
    vehicleTitleText: "Skåpbil hela vägen",
    vehicleDescText:
      "Vi kör hela sträckan själva med filtar och spännband – oavsett om du flyttar tre kvarter eller fem mil.",
  },

  itemsSection: {
    heading: "Vanliga saker i en flytt i Alingsås",
    items: [
      {
        name: "Soffgrupper",
        description: "Soffor, fåtöljer och fotpallar som skyddas med filt hela vägen",
        image: "/sofa.webp",
        alt: "Soffa som bärs ut vid flytthjälp i Alingsås",
      },
      {
        name: "Sovrumsmöbler",
        description: "Sängar, madrasser, nattduksbord och spjälsängar",
        image: "/bed.webp",
        alt: "Säng som flyttas från villa i Ingared, Alingsås",
      },
      {
        name: "Köksbord & stolar",
        description: "Matgrupper, köksbänkar och barstolar",
        image: "/table.webp",
        alt: "Köksbord som lastas vid flytt i Noltorp, Alingsås",
      },
      {
        name: "Vitvaror",
        description: "Tvättmaskin, torktumlare, kyl och frys – säkrade under körningen",
        image: "/washer.webp",
        alt: "Kylskåp och tvättmaskin som flyttas i Sollebrunn, Alingsås",
      },
      {
        name: "Skåp & byråer",
        description: "Garderober, linneskåp, byråer och bokhyllor",
        image: "/wardrobe.webp",
        alt: "Garderob som bärs ut ur trähus i centrala Alingsås",
      },
      {
        name: "Lådor & säckar",
        description: "Flyttkartonger, klädsäckar och saker från förråd och vind",
        image: "/box.webp",
        alt: "Flyttkartonger som bärs vid flytthjälp i Västerbodarna, Alingsås",
      },
    ],
  },

  seoContent: {
    mainHeading: "Flytthjälp i Alingsås – trästad, villabyar och pendlarorter",
    mainText:
      "Alingsås kommun har runt 43 000 invånare, varav drygt 27 000 i själva staden mellan sjöarna Mjörn och Gerdsken. Centrum är en trästad med gårdar och smala gårdsinfarter, där äldre hus ofta har branta trappor och låga dörrkarmar. Runt centrum ligger stadsdelar som Brogården, Noltorp, Stadsskogen, Kullingsberg, Lövekulle och Östlyckan, med en blandning av flerbostadshus, radhus och villor. E20 går rakt genom staden, vilket gör det enkelt att ta sig in och ut med skåpbilen men kräver planering av var vi stannar i centrum.\n\nUtanför staden är det småhus som gäller. Ingared, vid norra änden av Sävelången drygt en mil sydväst om centrum, består nästan bara av villaområden som byggdes från mitten av 1970-talet. Västerbodarna har pendeltågsstation på Västra stambanan, och i norr ligger Sollebrunn och Gräfsnäs med gårdar och hus på större tomter. Där är bärvägarna ofta längre och förråden större, så vi räknar med fler vändor och mer tid per flytt än i en lägenhet.\n\nMånga som flyttar till Alingsås kommer från Göteborg och vill ha mer boyta till lägre pris, men ändå kunna pendla. Pendeltåget tar runt 40 minuter till Göteborg C, och med bil är det drygt fyra mil längs E20. Eftersom sträckan är längre än de 20 km som ingår i paketet tillkommer ett km-pris beroende på paketstorlek – det räknas in direkt i bokningen, så du ser hela kostnaden innan du bekräftar.",
    preparationHeading: "Förbered flytten i Alingsås",
    preparationText:
      "Gör en lista över vad som ska med innan du bokar – det gör det lättare att välja rätt paket. Packa kartongerna så att de går att stapla och märk dem med rum. Porslin och glas packas tätt med tidningspapper eller handdukar, och tunga saker läggs i små lådor.\n\nBor du i ett äldre trähus i centrum: mät trappan och dörrarna om du är osäker på om en stor soffa eller garderob går igenom, så kan vi demontera i förväg. Flyttar du från villa i Ingared, Sollebrunn eller Västerbodarna: töm förrådet och garaget på det som inte ska med och se till att skåpbilen kan komma nära huset. På landsbygden är det bra om du beskriver infarten i bokningen, särskilt om vägen är smal eller grusad.",
    additionalSections: [
      {
        heading: "Flytta från Göteborg till Alingsås",
        content:
          "En flytt från Göteborg till Alingsås är längre än de flesta vi gör i regionen, men upplägget är detsamma. Vi bär ut och lastar i Göteborg, kör E20 förbi Partille och Lerum och bär in i nya bostaden i Alingsås. För större bohag planerar vi dagen så att allt ryms i en eller två laster – det håller nere både körtiden och priset. Det tar också hand om den vanliga frågan om bilen räcker: vi väljer upplägg efter det du anger i bokningen.",
        cta: { text: "Se flytthjälp i Göteborg →", href: "/flytthjalp-goteborg" },
      },
      {
        heading: "Stadsdelar och orter vi flyttar i",
        content:
          "I staden flyttar vi i centrum och trästaden, Brogården, Noltorp, Nolby, Stadsskogen, Kullingsberg, Lövekulle, Östlyckan, Bolltorp och Kristineholm. Utanför staden tar vi flyttar i Ingared, Västerbodarna, Sollebrunn, Gräfsnäs, Hemsjö och Stora Mellby, och även på gårdar och fritidshus i kommunens norra delar.\n\nFlytthjälp ger RUT-avdrag: 50 % av arbetskostnaden, upp till 75 000 kr per person och år. Vi drar avdraget på fakturan, så du betalar bara din del.",
      },
      {
        heading: "Varför boka flytthjälp i Alingsås med Ungdomskraft?",
        listItems: [
          {
            label: "Hela sträckan i priset –",
            text: "Km-priset för en längre flytt räknas ut direkt. Du vet vad en flytt från Göteborg till Alingsås kostar innan du bokar.",
          },
          {
            label: "Vana vid villaflytt –",
            text: "I Ingared, Sollebrunn och Västerbodarna är det mest villor med garage och förråd. Vi planerar för det från början.",
          },
          {
            label: "Varsamma i trästaden –",
            text: "Vi skyddar trappor, dörrkarmar och golv i äldre hus med filtar och mattor.",
          },
          {
            label: "Försäkrat –",
            text: "Alla flyttar är försäkrade genom Trygg Hansa.",
          },
          {
            label: "Flexibla tider –",
            text: "Vi flyttar alla dagar i veckan, också helger, och har ofta lediga tider med kort varsel.",
          },
        ],
      },
    ],
  },

  faq: [
    {
      question: "Vad kostar flytthjälp i Alingsås?",
      answer:
        "Du väljer paket efter mängd: Stor (2 495 kr) för en handfull möbler, Extra Stor (4 995 kr, upp till 4 timmar) för en mindre lägenhet och Mega Stor (8 995 kr, upp till 8 timmar) för en villa. Inom 20 km ingår körningen; längre sträckor får ett km-pris som syns i bokningen.",
    },
    {
      question: "Vad kostar det att flytta från Göteborg till Alingsås?",
      answer:
        "Sträckan är drygt fyra mil, alltså längre än de 20 km som ingår. Därför tillkommer ett km-pris som beror på paketet (8–22 kr/km för skåpbilspaketen). Hela beloppet räknas ut i bokningen innan du bekräftar.",
    },
    {
      question: "Får jag RUT-avdrag på flytthjälp i Alingsås?",
      answer:
        "Ja, flytthjälp är RUT-berättigad. Avdraget är 50 % av arbetskostnaden, upp till 75 000 kr per person och år, och vi gör det direkt på fakturan.",
    },
    {
      question: "Flyttar ni i Ingared och Västerbodarna?",
      answer:
        "Ja. Båda orterna består till stor del av villor, och där tömmer vi hus, garage och förråd. Vi kör dit via E20 och kan flytta både inom kommunen och till och från Göteborg.",
    },
    {
      question: "Kan ni flytta på landsbygden kring Sollebrunn och Gräfsnäs?",
      answer:
        "Ja, vi flyttar i hela kommunen, också på gårdar och hus längre ut. Beskriv infarten i bokningen om vägen är smal eller om bilen inte kan stå nära huset, så planerar vi bärvägen.",
    },
    {
      question: "Bär ni i äldre hus med smala trappor i centrala Alingsås?",
      answer:
        "Ja. Vi skyddar trappan och dörrkarmarna och tar isär möbler som inte går hela genom trappan. Det ingår i flytten.",
    },
    {
      question: "Ingår skåpbil?",
      answer: "Ja, skåpbil och bärare ingår alltid i flytthjälpen.",
    },
    {
      question: "Hur många bärare skickar ni?",
      answer:
        "Normalt två personer och en skåpbil. Vid stora flyttar planerar vi fler vändor eller en längre arbetsdag.",
    },
    {
      question: "Kan ni ta isär och sätta ihop möbler?",
      answer:
        "Ja, vi demonterar sängar, garderober och bord och monterar ihop dem igen på nya adressen.",
    },
    {
      question: "Flyttar ni på helger?",
      answer: "Ja, vi flyttar alla dagar i veckan, även lördag och söndag.",
    },
    {
      question: "Är flytten försäkrad?",
      answer: "Ja, alla flyttar är försäkrade genom Trygg Hansa.",
    },
    {
      question: "Kan ni flytta från Alingsås till Lerum eller Partille?",
      answer:
        "Ja, vi flyttar mellan alla kommuner i Göteborgsregionen. Lerum och Partille ligger längs samma E20-sträcka som Alingsås, så det är korta och vanliga flyttar för oss.",
    },
    {
      question: "Vilka delar av Alingsås täcker ni?",
      answer:
        "Hela kommunen: centrum, Brogården, Noltorp, Nolby, Stadsskogen, Kullingsberg, Lövekulle, Östlyckan, Bolltorp och Kristineholm i staden, samt Ingared, Västerbodarna, Sollebrunn, Gräfsnäs, Hemsjö och Stora Mellby.",
    },
    {
      question: "Vilka kommuner flyttar ni i utöver Alingsås?",
      answer: `Vi flyttar i hela Göteborgsregionen: ${goteborgKommunerText}.`,
    },
  ],

  priceHint: PACKAGE_PRICE_HINT_SV.transport,

  pricingRows: getPackagePricingRows("transport", "sv"),

  dynamicHeadings: {
    threeSteps: "Flytthjälp i Alingsås – tre steg",
    step2Text:
      "Vi bär ut och lastar hos dig, kör till nya bostaden i Alingsås eller var den nu ligger och bär in allt. Körningen är redan inräknad i priset.",
    step3Title: "Möblerna på plats",
    step3Desc:
      "Allt står där du vill ha det och det vi monterade isär är ihopsatt igen. Fakturan kommer med RUT-avdraget redan draget.",
    ctaHeading: "Boka flytthjälp i Alingsås",
    ctaText:
      "Ange adresserna och bohaget – du ser priset inklusive körsträcka direkt.",
  },

  en: {
    slug: "moving-help-alingsas",
    meta: {
      title: "Moving Help Alingsås – Move with a Van | Ungdomskraft",
      description:
        "Book moving help in Alingsås, Ingared, Sollebrunn and Västerbodarna. Van and carriers included, price shown instantly and RUT taken off the invoice.",
      h1: "Moving Help Alingsås",
    },
    hero: {
      heading: "Moving Help in Alingsås",
      intro:
        "Moving to, from or within Alingsås? We come with a van and carriers to the wooden houses in the centre, the flats in Brogården, the terraces and houses in the districts around the centre, the residential streets of Ingared by Lake Sävelången or the farms around Sollebrunn and Gräfsnäs. The whole move in one package – the price shows before you book and the RUT deduction is made directly on the invoice.",
      heroImageAlt: "Ungdomskraft loading furniture into a van during moving help in Alingsås",
    },
    featureCards: {
      sinceText: "Moving help since 2014",
      storyText:
        "Ungdomskraft has moved households in the Gothenburg region since 2014. Alingsås sits in the region's north-eastern corner, and we drive the E20 here regularly – both with families leaving Gothenburg and with locals changing homes within the municipality.",
      bookYourText: "Book your move",
      bookDescText:
        "Fill in the addresses, choose what needs moving and a day that suits you. The distance is added instantly, so the price you see is the price you pay.",
      vehicleTitleText: "Van all the way",
      vehicleDescText:
        "We drive the whole distance ourselves with blankets and straps – whether you are moving three blocks or 50 km.",
    },
    itemsSection: {
      heading: "Common items in a move in Alingsås",
      items: [
        {
          name: "Sofa sets",
          description: "Sofas, armchairs and footstools protected with blankets all the way",
          image: "/sofa.webp",
          alt: "Sofa carried out during moving help in Alingsås",
        },
        {
          name: "Bedroom furniture",
          description: "Beds, mattresses, bedside tables and cots",
          image: "/bed.webp",
          alt: "Bed moved from a house in Ingared, Alingsås",
        },
        {
          name: "Kitchen tables & chairs",
          description: "Dining sets, kitchen islands and bar stools",
          image: "/table.webp",
          alt: "Kitchen table loaded during a move in Noltorp, Alingsås",
        },
        {
          name: "Appliances",
          description: "Washer, dryer, fridge and freezer – secured during the drive",
          image: "/washer.webp",
          alt: "Fridge and washing machine moved in Sollebrunn, Alingsås",
        },
        {
          name: "Cabinets & dressers",
          description: "Wardrobes, linen cupboards, dressers and bookcases",
          image: "/wardrobe.webp",
          alt: "Wardrobe carried out of a wooden house in central Alingsås",
        },
        {
          name: "Boxes & bags",
          description: "Moving boxes, clothes bags and things from storage and the attic",
          image: "/box.webp",
          alt: "Moving boxes carried during moving help in Västerbodarna, Alingsås",
        },
      ],
    },
    seoContent: {
      mainHeading: "Moving help in Alingsås – wooden town, villages and commuter towns",
      mainText:
        "Alingsås municipality has around 43,000 residents, just over 27,000 of them in the town itself between the lakes Mjörn and Gerdsken. The centre is a wooden town with courtyards and narrow courtyard entrances, where older houses often have steep stairs and low door frames. Around the centre lie districts such as Brogården, Noltorp, Stadsskogen, Kullingsberg, Lövekulle and Östlyckan, with a mix of apartment buildings, terraces and houses. The E20 runs straight through the town, which makes it easy to get in and out with the van but means we plan where to stop in the centre.\n\nOutside the town it is all houses. Ingared, at the northern end of Lake Sävelången a little over 10 km south-west of the centre, is almost entirely made up of residential areas built from the mid-1970s. Västerbodarna has a commuter train station on the Western Main Line, and to the north lie Sollebrunn and Gräfsnäs with farms and houses on larger plots. There the carrying routes are often longer and the storage bigger, so we allow for more trips and more time per move than in a flat.\n\nMany people moving to Alingsås come from Gothenburg and want more space for less money while still being able to commute. The commuter train takes around 40 minutes to Gothenburg Central, and by car it is just over 40 km along the E20. Because the distance is longer than the 20 km included in the package, a per-km price depending on package size is added – it is calculated directly in the booking, so you see the full cost before you confirm.",
      preparationHeading: "Prepare your move in Alingsås",
      preparationText:
        "Make a list of what is coming before you book – it makes it easier to pick the right package. Pack boxes so they can be stacked and label them by room. Pack china and glass tightly with newspaper or towels, and put heavy things in small boxes.\n\nIf you live in an older wooden house in the centre: measure the stairs and doors if you are unsure whether a big sofa or wardrobe will fit, so we can disassemble in advance. Moving from a house in Ingared, Sollebrunn or Västerbodarna: clear the shed and garage of what isn't coming and make sure the van can get close to the house. In the countryside it helps if you describe the driveway in the booking, especially if the road is narrow or gravel.",
      additionalSections: [
        {
          heading: "Moving from Gothenburg to Alingsås",
          content:
            "A move from Gothenburg to Alingsås is longer than most we do in the region, but the approach is the same. We carry out and load in Gothenburg, drive the E20 past Partille and Lerum and carry into the new home in Alingsås. For larger households we plan the day so everything fits in one or two loads – that keeps both the driving time and the price down. It also settles the usual question of whether the van is big enough: we plan based on what you enter in the booking.",
          cta: { text: "See moving help in Gothenburg →", href: "/en/moving-help-gothenburg" },
        },
        {
          heading: "Districts and villages we move in",
          content:
            "In town we move in the centre and the wooden town, Brogården, Noltorp, Nolby, Stadsskogen, Kullingsberg, Lövekulle, Östlyckan, Bolltorp and Kristineholm. Outside town we take moves in Ingared, Västerbodarna, Sollebrunn, Gräfsnäs, Hemsjö and Stora Mellby, and also on farms and holiday homes in the northern parts of the municipality.\n\nMoving help qualifies for RUT: 50% of the labour cost, up to SEK 75,000 per person per year. We deduct it on the invoice, so you only pay your share.",
        },
        {
          heading: "Why book moving help in Alingsås with Ungdomskraft?",
          listItems: [
            {
              label: "The whole distance in the price –",
              text: "The per-km price for a longer move is calculated instantly. You know what a move from Gothenburg to Alingsås costs before you book.",
            },
            {
              label: "Used to house moves –",
              text: "Ingared, Sollebrunn and Västerbodarna are mostly houses with garages and sheds. We plan for that from the start.",
            },
            {
              label: "Careful in the wooden town –",
              text: "We protect stairs, door frames and floors in older houses with blankets and mats.",
            },
            {
              label: "Insured –",
              text: "Every move is insured through Trygg Hansa.",
            },
            {
              label: "Flexible times –",
              text: "We move every day of the week, weekends too, and often have slots at short notice.",
            },
          ],
        },
      ],
    },
    faq: [
      {
        question: "How much does moving help in Alingsås cost?",
        answer:
          "You choose a package by volume: Large (SEK 2,495) for a handful of furniture, Extra Large (SEK 4,995, up to 4 hours) for a smaller flat and Mega Large (SEK 8,995, up to 8 hours) for a house. Driving within 20 km is included; longer distances get a per-km price shown in the booking.",
      },
      {
        question: "What does it cost to move from Gothenburg to Alingsås?",
        answer:
          "The distance is just over 40 km, longer than the 20 km included. A per-km price depending on the package is therefore added (SEK 8–22/km for the van packages). The full amount is calculated in the booking before you confirm.",
      },
      {
        question: "Do I get the RUT deduction on moving help in Alingsås?",
        answer:
          "Yes, moving help qualifies for RUT. The deduction is 50% of the labour cost, up to SEK 75,000 per person per year, and we apply it directly on the invoice.",
      },
      {
        question: "Do you move in Ingared and Västerbodarna?",
        answer:
          "Yes. Both are largely made up of houses, where we empty homes, garages and sheds. We drive there via the E20 and can move within the municipality as well as to and from Gothenburg.",
      },
      {
        question: "Can you move in the countryside around Sollebrunn and Gräfsnäs?",
        answer:
          "Yes, we move across the whole municipality, including farms and houses further out. Describe the driveway in the booking if the road is narrow or the van can't park near the house, and we will plan the carrying route.",
      },
      {
        question: "Do you carry in older houses with narrow stairs in central Alingsås?",
        answer:
          "Yes. We protect the stairs and door frames and take apart furniture that won't fit through the stairwell whole. That is included in the move.",
      },
      {
        question: "Is a van included?",
        answer: "Yes, a van and carriers are always included in moving help.",
      },
      {
        question: "How many carriers do you send?",
        answer:
          "Normally two people and a van. For large moves we plan more trips or a longer working day.",
      },
      {
        question: "Can you take apart and reassemble furniture?",
        answer:
          "Yes, we disassemble beds, wardrobes and tables and put them back together at the new address.",
      },
      {
        question: "Do you move at weekends?",
        answer: "Yes, we move every day of the week, including Saturday and Sunday.",
      },
      {
        question: "Is the move insured?",
        answer: "Yes, every move is insured through Trygg Hansa.",
      },
      {
        question: "Can you move me from Alingsås to Lerum or Partille?",
        answer:
          "Yes, we move between all municipalities in the Gothenburg region. Lerum and Partille lie along the same E20 corridor as Alingsås, so these are short and common moves for us.",
      },
      {
        question: "Which parts of Alingsås do you cover?",
        answer:
          "The whole municipality: the centre, Brogården, Noltorp, Nolby, Stadsskogen, Kullingsberg, Lövekulle, Östlyckan, Bolltorp and Kristineholm in town, plus Ingared, Västerbodarna, Sollebrunn, Gräfsnäs, Hemsjö and Stora Mellby.",
      },
      {
        question: "Which municipalities do you move in besides Alingsås?",
        answer: `We move across the whole Gothenburg region: ${goteborgKommunerText}.`,
      },
    ],
    priceHint: PACKAGE_PRICE_HINT_EN.transport,
    pricingRows: getPackagePricingRows("transport", "en"),
    dynamicHeadings: {
      threeSteps: "Moving help in Alingsås – three steps",
      step2Text:
        "We carry out and load at your place, drive to the new home in Alingsås or wherever it is and carry everything in. The driving is already included in the price.",
      step3Title: "Furniture in place",
      step3Desc:
        "Everything stands where you want it and whatever we took apart is back together. The invoice arrives with the RUT deduction already applied.",
      ctaHeading: "Book moving help in Alingsås",
      ctaText:
        "Enter the addresses and your household – you see the price including distance instantly.",
    },
  },
};
